import { useEffect, useMemo, useState, useRef } from "react";
import { useRoute, useLocation, useSearch } from "wouter";
import { projects as mockProjects } from "../../data/mock";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useInPageActionVisibility } from "../../lib/useInPageActionVisibility";
import { useEscapeKey } from "../../lib/useEscapeKey";
import { useI18n } from "../../lib/i18n";
import type { AwardOpts } from "../../components/AwardModal";
import { TABS, CAN_BID, durationInDays, type BidDocument, type ProjectQuestionMessage, type BidCompany, buildModel, money, type ProjectLoadState, getErrorStatus } from "./model";

export function useProjectDetail() {
  const { t } = useI18n();
  const [, params] = useRoute("/project/:id");
  const [, navigate] = useLocation();
  const search = useSearch();
  const { user } = useAuth();
  const id = params?.id || "";

  const [item, setItem] = useState<any | null>(null);
  const [loadState, setLoadState] = useState<ProjectLoadState>("loading");
  const [contactOpen, setContactOpen] = useState(false); // [30] in-page composer
  const [tab, setTab] = useState("Project Details");
  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);

  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(viewport.matches);
    viewport.addEventListener("change", updateViewport);
    return () => viewport.removeEventListener("change", updateViewport);
  }, []);

  // bids state
  const [bidData, setBidData] = useState<Awaited<ReturnType<typeof api.getBids>>>(null as any);
  const [projectContract, setProjectContract] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [awardTarget, setAwardTarget] = useState<any | null>(null); // v3.9.58 — bid being awarded (milestone modal)
  const [bidSort, setBidSort] = useState<"lowest" | "shortest">("lowest");
  const bidSortMenu = useRef<HTMLDetailsElement>(null);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [termsTarget, setTermsTarget] = useState<{ bidderName: string; terms: string; trigger: HTMLButtonElement } | null>(null);
  const [documentsTarget, setDocumentsTarget] = useState<{ bidId: string; bidderName: string; documents: BidDocument[] } | null>(null);

  // attachments + Q&A (Increment 6)
  const [attachments, setAttachments] = useState<any[]>([]);
  const [questions, setQuestions] = useState<ProjectQuestionMessage[]>([]);
  const [bidCompanies, setBidCompanies] = useState<BidCompany[]>([]);
  const [bidCompaniesLoaded, setBidCompaniesLoaded] = useState(false);

  // submit form
  const [form, setForm] = useState({ price: "", durationText: "", terms: "" });
  const [bidFiles, setBidFiles] = useState<File[]>([]);
  const [bidComposerOpen, setBidComposerOpen] = useState(false);
  const handledReviseLink = useRef<string | null>(null);
  const [legalTerms, setLegalTerms] = useState<Awaited<ReturnType<typeof api.legalDoc>> | null>(null);
  const [legalTermsState, setLegalTermsState] = useState<"loading" | "ready" | "unavailable">("loading");
  const [bidReviewConfirmed, setBidReviewConfirmed] = useState(false);

  useEffect(() => {
    const closeSortMenu = (event: MouseEvent) => {
      if (bidSortMenu.current && !bidSortMenu.current.contains(event.target as Node)) bidSortMenu.current.removeAttribute("open");
    };
    document.addEventListener("mousedown", closeSortMenu);
    return () => document.removeEventListener("mousedown", closeSortMenu);
  }, []);

  const p = useMemo(() => buildModel(id, item), [id, item]);
  const canBid = !!user && CAN_BID.some((c) => user.capabilities?.includes(c));
  const isProjectOwner = !!user && p?.ownerUserId != null && p.ownerUserId === user.id;

  useEffect(() => {
    const requestedTab = new URLSearchParams(search).get("tab");
    if (requestedTab && TABS.includes(requestedTab)) setTab(requestedTab);
  }, [search]);

  async function loadExtras() {
    try {
      const [a, q] = await Promise.all([api.listAttachments(id), api.listQuestions(id)]);
      setAttachments(a?.attachments || []);
      setQuestions(q?.messages || []);
    } catch { /* keep */ }
  }
  useEffect(() => { loadExtras(); /* eslint-disable-next-line */ }, [id]);

  async function loadBids() {
    if (!user) { setBidData(null as any); return; }
    try { setBidData(await api.getBids(id)); } catch { /* keep */ }
  }

  useEffect(() => {
    let alive = true;
    setLoadState("loading");
    api.getProject(id)
      .then((it) => {
        if (!alive) return;
        setItem(it);
        setLoadState(it || mockProjects.some((project) => project.id === id) ? "ready" : "notfound");
      })
      .catch((error: unknown) => {
        if (!alive) return;
        setItem(null);
        if (mockProjects.some((project) => project.id === id)) setLoadState("ready");
        else setLoadState(getErrorStatus(error) === 404 ? "notfound" : "unavailable");
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { loadBids(); /* eslint-disable-next-line */ }, [id, user?.id]);

  useEffect(() => {
    if (!bidComposerOpen) return;
    let active = true;
    setLegalTerms(null);
    setLegalTermsState("loading");
    api.legalDoc("terms")
      .then((document) => {
        if (!active) return;
        setLegalTerms(document);
        setLegalTermsState(document ? "ready" : "unavailable");
      })
      .catch(() => { if (active) { setLegalTerms(null); setLegalTermsState("unavailable"); } });
    return () => { active = false; };
  }, [bidComposerOpen]);

  useEffect(() => {
    if (!user) { setProjectContract(null); return; }
    let active = true;
    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      const [projectResult, bidResult, contractResult] = await Promise.allSettled([
        api.getProject(id), api.getBids(id), api.listContracts(),
      ]);
      if (!active) return;
      if (projectResult.status === "fulfilled" && projectResult.value) setItem(projectResult.value);
      if (bidResult.status === "fulfilled") setBidData(bidResult.value);
      if (contractResult.status === "fulfilled") {
        setProjectContract(contractResult.value.contracts.find((contract) => contract.sourceType === "project" && contract.sourceId === id) || null);
      }
    };
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 30_000);
    document.addEventListener("visibilitychange", refresh);
    return () => { active = false; window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [id, user?.id]);

  useEffect(() => {
    let alive = true;
    setBidCompaniesLoaded(false);
    if (!user) { setBidCompanies([]); setBidCompaniesLoaded(true); return () => { alive = false; }; }
    api.myCompanies()
      .then((companies) => {
        if (!alive) return;
        setBidCompanies(companies.map((company) => ({ id: String(company.id), name: String(company.name || "Company") })));
        setBidCompaniesLoaded(true);
      })
      .catch(() => { if (alive) { setBidCompanies([]); setBidCompaniesLoaded(true); } });
    return () => { alive = false; };
  }, [user?.id]);

  const isOwner = !!bidData?.isOwner;
  useEffect(() => {
    if (isOwner && !new URLSearchParams(search).get("tab")) setTab("Bidding");
  }, [isOwner, search]);
  const myBid = (bidData as any)?.myBid || null;
  const canMessageClient = !!user && p?.ownerUserId != null && p.ownerUserId !== user.id
    && (!!myBid && ["submitted", "shortlisted", "in_negotiation", "awarded"].includes(myBid.status)
      || !!projectContract && ["pending_funding", "funded"].includes(projectContract.status));
  const awarded = p?.status === "awarded" || bidData?.status === "awarded";
  const biddingClosed = !!(item?.biddingClosed ?? bidData?.biddingClosed);
  const canEditBid = !!bidData?.canEditBid && !isOwner;
  const sortedBids = [...(bidData?.bids || [])].sort((a: any, b: any) => {
    if (bidSort === "shortest") {
      const byDuration = durationInDays(a.durationText) - durationInDays(b.durationText);
      if (!Number.isNaN(byDuration) && byDuration !== 0) return byDuration;
    }
    return (a.standingPrice ?? a.price) - (b.standingPrice ?? b.price);
  });
  const winningBid = bidData?.bids?.find((bid: any) => bid.status === "awarded");
  const personalName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Personal account";
  const activeCompanyContext = user?.activeContext?.startsWith("company:") ? user.activeContext : null;
  const selectedBidCompany = activeCompanyContext
    ? bidCompanies.find((company) => `company:${company.id}` === activeCompanyContext)
    : null;
  const selectedBidderName = activeCompanyContext
    ? selectedBidCompany?.name || (bidCompaniesLoaded ? "Selected company unavailable" : "Loading selected company…")
    : personalName;
  const { actionRef: bidRef, isVisible } = useInPageActionVisibility<HTMLDivElement>(id);
  useEscapeKey(() => setBidComposerOpen(false), bidComposerOpen && !busy);

  async function submitBid(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (legalTermsState !== "ready" || !legalTerms || !bidReviewConfirmed) { setErr("Review the published Terms of Service before submitting."); return; }
    if (activeCompanyContext && !selectedBidCompany) { setErr("The selected company is unavailable. Switch accounts or try again."); return; }
    const price = money(form.price);
    if (price <= 0) { setErr("Please enter a valid bid price."); return; }
    setBusy(true);
    try {
      const { bid } = await api.submitBid(id, {
        price,
        durationText: form.durationText || undefined,
        terms: form.terms || undefined,
        company: !!selectedBidCompany,
        bidderName: selectedBidCompany?.name,
      });
      for (let index = 0; index < bidFiles.length; index++) {
        try { await api.uploadBidDocument(id, bid.id, bidFiles[index]); }
        catch (uploadError: unknown) {
          setBidFiles(bidFiles.slice(index));
          await loadBids();
          setErr(`Bid saved, but ${bidFiles[index].name} could not upload: ${uploadError instanceof Error ? uploadError.message : "Try again."}`);
          return;
        }
      }
      setBidFiles([]);
      setBidComposerOpen(false);
      setBidReviewConfirmed(false);
      // refresh item (for the live public count) + sealed bid state
      const it = await api.getProject(id);
      setItem(it);
      await loadBids();
      const returnTo = new URLSearchParams(search).get("returnTo");
      if (returnTo && /^\/messages\/[A-Za-z0-9_-]+$/.test(returnTo)) navigate(returnTo);
    } catch (e: any) {
      setErr(e?.message || "Could not submit your bid.");
    } finally {
      setBusy(false);
    }
  }

  async function shortlist(bidId: string) {
    setBusy(true);
    try { await api.shortlistBid(id, bidId); await loadBids(); } catch (e: any) { setErr(e?.message || "Action failed."); } finally { setBusy(false); }
  }
  async function award(bidId: string, opts?: AwardOpts) {
    setBusy(true);
    try {
      const response = await api.awardBid(id, bidId, opts);
      setAwardTarget(null);
      navigate(`/messages/${encodeURIComponent(response.agreement.threadId)}?agreement=${encodeURIComponent(response.agreement.negotiationId)}`);
    } catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not send the agreement proposal."); throw error; } finally { setBusy(false); }
  }

  async function removeAttachment(attId: string) {
    try { await api.deleteAttachment(id, attId); await loadExtras(); } catch { /* ignore */ }
  }

  function openBidComposer() {
    if (!user) { navigate("/login"); return; }
    if (!canBid) { navigate("/dashboard"); return; }
    if (!canEditBid) return;
    if (myBid) setForm((current) => ({ ...current, price: String(myBid.standingPrice ?? myBid.price), durationText: myBid.durationText || "", terms: myBid.terms || "" }));
    setErr(null);
    setBidReviewConfirmed(false);
    setBidFiles([]);
    setBidComposerOpen(true);
  }

  useEffect(() => {
    if (new URLSearchParams(search).get("action") !== "revise-bid" || !myBid || !canEditBid) return;
    const key = `${id}:${search}`;
    if (handledReviseLink.current === key) return;
    handledReviseLink.current = key;
    openBidComposer();
    // The deep link runs once after the bidder's bid and edit permission have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, search, myBid?.id, canEditBid]);

  async function downloadBidDocument(bidId: string, document: BidDocument) {
    try { await api.downloadBidDocument(id, bidId, document); }
    catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not download this document."); }
  }

  async function removeBidDocument(documentId: string) {
    if (!myBid) return;
    setBusy(true);
    try { await api.deleteBidDocument(id, myBid.id, documentId); await loadBids(); }
    catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not remove this document."); }
    finally { setBusy(false); }
  }

  return { t, navigate, user, id, item, loadState, contactOpen, setContactOpen, tab, setTab, shareMsg, setShareMsg, isMobileViewport, bidData, projectContract, busy, err, awardTarget, setAwardTarget, bidSort, setBidSort, bidSortMenu, showAllFiles, setShowAllFiles, termsTarget, setTermsTarget, documentsTarget, setDocumentsTarget, attachments, questions, form, setForm, bidFiles, setBidFiles, bidComposerOpen, setBidComposerOpen, legalTerms, legalTermsState, bidReviewConfirmed, setBidReviewConfirmed, p, isProjectOwner, loadExtras, isOwner, myBid, canMessageClient, awarded, biddingClosed, canEditBid, sortedBids, winningBid, activeCompanyContext, selectedBidCompany, selectedBidderName, bidRef, isVisible, submitBid, shortlist, award, removeAttachment, openBidComposer, downloadBidDocument, removeBidDocument };
}

export type ProjectDetailState = ReturnType<typeof useProjectDetail>;
